import { TransformNumberPipe } from '@app/@shared/pipes/transform-number.pipe';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { ControlPanelStore } from '../control-panel-service/control-panel.store';
import jsPDF from 'jspdf';
import autoTable, { Table } from 'jspdf-autotable';
import moment from 'moment';
import {
  ActionAgentExcelData,
  ActionAgentMonth,
  ActionAgentReportDTO,
  ActionAgentReportResponseDTO,
  ActionAgentTotal,
  AgentActionPlanClass,
  AgentActionPlanDTO,
  AgentActionPlanSearchDTO,
  AgentType,
  CustomerDetail,
  PdfSearchDTO,
} from '../models/control-panel.model';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { pairwise, startWith } from 'rxjs/operators';
import { MAX_SELECTOR_DATA } from '@app/@core/constants/material-custom.constants';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import FileSaver from 'file-saver';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import {
  BUSINESS_DIRECTOR,
  CHAIN,
  MACRO_CHAIN,
  MACRO_OFFICE,
  OFFICE_DIRECTOR,
} from '@app/@core/constants/users.constants';
import { Credentials } from '@app/@core/models/login-models';
import { TranslateService } from '@ngx-translate/core';
import { PageEvent } from '@angular/material/paginator';
import { DATE_FORMAT_DDMMYYYY, LANG_ES } from '@app/@core/constants/common.constants';

interface TableData {
  title: string;
  header: Array<string[]>;
  column: Array<number[]>;
}

const MAX_NUM_ROWS: number = 22;
const CURRENT_YEAR: number = 0;
const LAST_YEAR: number = 1;

const FORM_PARAM_MACRO_CHAIN = 'macroChain';
const FORM_PARAM_MACRO_OFFICE = 'macroOffice';

const emptyCommonDataDTO: CommonDataDTO = {
  id: '',
  value: '',
};

const MIN_PAGE_SIZE: number = 50;

@Component({
  selector: 'app-action-agent',
  templateUrl: './action-agent.component.html',
  styleUrls: ['./action-agent.component.scss'],
})
export class ActionAgentComponent implements OnInit {
  @ViewChild('group') cdkVirtualScrollViewPort: CdkVirtualScrollViewport;

  actionAgentForm: FormGroup;

  divisionList: CommonDataDTO[] = [];
  officeList: CommonDataDTO[] = [];
  groupList: CommonDataDTO[] = [];
  nifcifList: CommonDataDTO[] = [];
  pointOfSaleList: CommonDataDTO[] = [];
  externalCommercialList: CommonDataDTO[] = [];

  radioButtonValues: string[] = [AgentType.Group, AgentType.NifCif, AgentType.Agent];

  response: ActionAgentReportResponseDTO;
  table: TableData[] = [];
  private tableStoreData: TableData[] = [];
  dataFormatted: any[] = [];
  headersTable: Array<string[]> = [[], []];

  private _maxPageIndex: number = 1;
  private _currentPageIndex: number = 1;
  readonly pageSize: number = MIN_PAGE_SIZE;
  itemsLength: number = 0;

  private isCalledOnce = false;

  columns: string[] = [];
  dataSource = new MatTableDataSource<any[]>();
  searchCriteria: AgentActionPlanSearchDTO;
  private allData: ActionAgentExcelData[] = [];

  userInfo: Credentials;
  macroChainList: CommonDataDTO[] = [];
  macroOfficeList: CommonDataDTO[] = [];
  isMacroOfficeUser: boolean = false;
  isMacroChainUser: boolean = false;
  isBusinessDirector: boolean = false;

  displayedCurrentColumns: string[] = [
    'headersCurrent',
    'totalCurrent',
    'januaryCurrent',
    'februaryCurrent',
    'marchCurrent',
    'aprilCurrent',
    'mayCurrent',
    'juneCurrent',
    'julyCurrent',
    'augustCurrent',
    'septemberCurrent',
    'octuberCurrent',
    'novemberCurrent',
    'decemberCurrent',
  ];

  displayedColumns: string[] = [
    'headers',
    'total',
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  firstColumns: string[] = [
    'actionAgent.investment',
    'actionAgent.numOperations',
    'actionAgent.percentageCO',
    'actionAgent.percentagePPT',
    'actionAgent.percentagePPB',
    'actionAgent.percentagePPP',
    'actionAgent.tirMedia',
    'actionAgent.totalCommission',
    'actionAgent.safeCommission',
    'actionAgent.feeCommission',
    'actionAgent.useInfo',
  ];

  constructor(
    private formBuilder: FormBuilder,
    private controlPanelStore: ControlPanelStore,
    private loaderGenericService: LoaderGenericService,
    private excelService: ExcelService,
    private numberPipe: TransformNumberPipe,
    private combosStore: CombosStore,
    private translateService: TranslateService
  ) {
    this.translateService.onLangChange.subscribe(() => {
      if (this.isCalledOnce) {
        this.updateSearchCriteria(this.actionAgentForm);
        this.searchCriteria = this.updateSearchCriteria(this.actionAgentForm);
      }
    });
  }

  ngOnInit(): void {
    this.loaderGenericService.showSpinner();
    let dataString: string = localStorage.getItem('credentials');
    this.userInfo = JSON.parse(dataString);
    this.actionAgentForm = this.formBuilder.group({
      division: ['', Validators.required],
      office: [''],
      group: [''],
      nifcif: [''],
      pointOfSale: [''],
      externalCommercial: [''],
      detail: [AgentType.Group, Validators.required],
    });

    this.columns = this.controlPanelStore.actionAgentTableColumn;

    this.validateMacroChainUser();
    if (this.isBusinessDirector) this.initMacroUsers([FORM_PARAM_MACRO_CHAIN, FORM_PARAM_MACRO_OFFICE]);
    else if (this.isMacroChainUser) this.initMacroUsers([FORM_PARAM_MACRO_CHAIN]);
    else if (this.isMacroOfficeUser) this.initMacroUsers([FORM_PARAM_MACRO_OFFICE]);
    else this.getAllCombos();
    this.setUpObservableValueChanges();
  }

  private validateMacroChainUser = () => {
    this.isMacroChainUser = this.userInfo.userType === MACRO_CHAIN;
    this.isMacroOfficeUser = this.userInfo.userType === MACRO_OFFICE;
    this.isBusinessDirector = this.userInfo.userType === BUSINESS_DIRECTOR;
  };

  private setUpObservableValueChanges = () => {
    this.actionAgentForm.controls.division.valueChanges.subscribe(() => {
      this.clearAll();
    });

    this.actionAgentForm.controls.office.valueChanges.pipe(startWith(), pairwise()).subscribe(([prev, next]) => {
      if (this.actionAgentForm.controls.office.enabled && prev !== next && next !== undefined && next !== null) {
        this.loaderGenericService.showSpinner();
        if (next === '') {
          this.clearGroup();
          this.clearNifcif();
          this.clearPointOfSale();
          this.clearExternalCommercial();
          this.getOffice();
        }

        Promise.all([this.getGroup(), this.getNifCif(), this.getPointOfSale(), this.getExternalCommercial()])
          .catch((error) => console.error(error))
          .finally(() => this.loaderGenericService.stopSpinner());
      }
    });

    this.actionAgentForm.controls.group.valueChanges.pipe(startWith(), pairwise()).subscribe(([prev, next]) => {
      if (this.actionAgentForm.controls.group.enabled && prev !== next && next !== undefined && next !== null) {
        this.loaderGenericService.showSpinner();
        if (next === '') {
          this.clearNifcif();
          this.clearPointOfSale();
          this.clearExternalCommercial();
          this.getGroup();
        }
        Promise.all([this.getOffice(), this.getNifCif(), this.getPointOfSale(), this.getExternalCommercial()])
          .catch((error) => console.error(error))
          .finally(() => this.loaderGenericService.stopSpinner());
      }
    });

    this.actionAgentForm.controls.nifcif.valueChanges.pipe(startWith(), pairwise()).subscribe(([prev, next]) => {
      if (this.actionAgentForm.controls.nifcif.enabled && prev !== next && next !== undefined && next !== null) {
        this.loaderGenericService.showSpinner();
        if (next === '') {
          this.clearPointOfSale();
          this.clearExternalCommercial();
          this.getNifCif();
        }
        Promise.all([this.getOffice(), this.getGroup(), this.getPointOfSale(), this.getExternalCommercial()])
          .catch((error) => console.error(error))
          .finally(() => this.loaderGenericService.stopSpinner());
      }
    });

    this.actionAgentForm.controls.pointOfSale.valueChanges.pipe(startWith(), pairwise()).subscribe(([prev, next]) => {
      if (this.actionAgentForm.controls.pointOfSale.enabled && prev !== next && next !== undefined && next !== null) {
        this.loaderGenericService.showSpinner();
        if (next === '') {
          this.clearExternalCommercial();
          this.getPointOfSale();
        }
        Promise.all([this.getOffice(), this.getGroup(), this.getNifCif(), this.getExternalCommercial()])
          .catch((error) => console.error(error))
          .finally(() => this.loaderGenericService.stopSpinner());
      }
    });

    this.actionAgentForm.controls.externalCommercial.valueChanges
      .pipe(startWith(), pairwise())
      .subscribe(([prev, next]) => {
        if (
          this.actionAgentForm.controls.externalCommercial.enabled &&
          prev !== next &&
          next !== undefined &&
          next !== null
        ) {
          this.loaderGenericService.showSpinner();
          if (next === '') {
            this.getExternalCommercial();
          }
          Promise.all([this.getOffice(), this.getGroup(), this.getPointOfSale(), this.getNifCif()])
            .catch((error) => console.error(error))
            .finally(() => this.loaderGenericService.stopSpinner());
        }
      });
  };

  getAllIdsFemName(): string {
    return this.translateService.instant('excel.allIdsFem');
  }

  consult(_isPaginationEvent: boolean = false): void {
    let request: AgentActionPlanDTO = null;
    if (!_isPaginationEvent) {
      this.clearConsultValues();

      const { division, office, group, nifcif, pointOfSale, externalCommercial, detail } =
        this.actionAgentForm.controls;
      request = new AgentActionPlanClass(
        division.value,
        office.value,
        group.value,
        nifcif.value,
        pointOfSale.value,
        externalCommercial.value,
        detail.value
      );
      request.pageIndex = this._currentPageIndex;
      request.pageSize = this.pageSize;
    } else {
      const { divisionId, officeId, chainId, prescriberId, establishmentId, externalCommercialId, customerDetail } =
        this.searchCriteria;
      request = new AgentActionPlanClass(
        divisionId.id,
        officeId.id,
        chainId.id,
        prescriberId.id,
        establishmentId.id,
        externalCommercialId.id,
        customerDetail
      );
      request.pageIndex = this._currentPageIndex;
      request.pageSize = this.pageSize;
    }

    this.controlPanelStore
      .getActionAgentReport(request)
      .then((response) => {
        const { groups, totalPages } = response;
        if (!this.response) {
          this.response = response;
        } else this.response.groups.push(...groups);

        if (totalPages > 0 && groups.length > 0) {
          this.itemsLength =
            totalPages === this._currentPageIndex ? this.response.groups.length : totalPages * this.pageSize;
          this.table = groups.map((group) => this.formatDataSource(group));

          this.tableStoreData.push(...this.table);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => (this.isCalledOnce = true));
  }

  updateSearchCriteria(form: FormGroup): AgentActionPlanSearchDTO {
    const { division, office, group, nifcif, pointOfSale, externalCommercial, detail } = form.controls;

    const divisionValue: CommonDataDTO = this.findComboDataByValue(division?.value, this.divisionList, {
      ...emptyCommonDataDTO,
      value: this.getAllIdsFemName(),
    });
    const officeValue: CommonDataDTO = this.findComboDataByValue(office?.value, this.officeList, {
      ...emptyCommonDataDTO,
      value: this.getAllIdsFemName(),
    });
    const groupValue: CommonDataDTO = this.findComboDataByValue(group?.value, this.groupList, {
      ...emptyCommonDataDTO,
      value: this.translateService.instant('excel.allIds'),
    });
    const nifcifValue: CommonDataDTO = this.findComboDataByValue(nifcif?.value, this.nifcifList, {
      ...emptyCommonDataDTO,
      value: this.translateService.instant('excel.allIds'),
    });
    const pointOfSaleValue: CommonDataDTO = this.findComboDataByValue(pointOfSale?.value, this.pointOfSaleList, {
      ...emptyCommonDataDTO,
      value: this.translateService.instant('excel.allIds'),
    });
    const externalCommercialValue: CommonDataDTO = this.findComboDataByValue(
      externalCommercial?.value,
      this.externalCommercialList,
      { ...emptyCommonDataDTO, value: this.translateService.instant('excel.allIds') }
    );
    const detailValue = detail.value;

    return {
      divisionId: divisionValue,
      officeId: officeValue,
      chainId: groupValue,
      prescriberId: nifcifValue,
      establishmentId: pointOfSaleValue,
      externalCommercialId: externalCommercialValue,
      customerDetail: detailValue,
      pageIndex: this._currentPageIndex,
      pageSize: this.pageSize,
    };
  }

  async print(): Promise<void> {
    this.getPreparedData(true);
    try {
      let doc = new jsPDF({
        orientation: 'portrait',
        format: [220, 296.926],
      });

      const filename =
        this.translateService.instant('actionAgent.pdf.title') + moment().locale(LANG_ES).format(DATE_FORMAT_DDMMYYYY);
      doc.setProperties({
        title: filename,
      });

      /* BODY */
      const numberOfPages = this.allData.length;
      this.allData.forEach((item) => {
        doc = this.createPDFTable(doc, item.column, numberOfPages, item.title);
      });

      const blob = doc.output('blob');
      FileSaver.saveAs(blob, filename);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  createPDFTable(doc: jsPDF, dataPdf: any, maxPages: number, tableTitle?: string): jsPDF {
    const lastAutoTable: Table = (doc as any).lastAutoTable; //la tabla anterior
    const maxYAllowedForTable: number = 255;
    const tableYCoordinateFirstPage: number = 85;
    const topMargin = 50;

    doc.setFontSize(10);

    let startYTable: number = tableYCoordinateFirstPage;
    if (lastAutoTable) {
      //If there's a previous table and there is no space for the new table, start from the top
      const { finalY } = lastAutoTable;
      const isStartingYNotAllowed: boolean = finalY + 10 > maxYAllowedForTable;

      if (isStartingYNotAllowed) doc.addPage();

      startYTable = isStartingYNotAllowed ? topMargin : finalY + 10;
    }

    this.searchCriteria = this.updateSearchCriteria(this.actionAgentForm); //para que aparezca "tots/totes" bien traducido
    //If there is no previous table, include the search filter.
    if (!lastAutoTable) {
      const secondRowYCoordinate: number = 45;
      const secondRowXCoordinate: number = 10;
      const { divisionId, officeId, chainId, prescriberId, establishmentId, externalCommercialId } =
        this.searchCriteria;

      const translationHeader: PdfSearchDTO = {
        division: { header: this.translateService.instant('excel.division'), body: divisionId?.value },
        office: { header: this.translateService.instant('excel.office'), body: officeId?.value },
        chain: { header: this.translateService.instant('excel.group'), body: chainId?.value },
        prescriber: { header: this.translateService.instant('excel.nifcif'), body: prescriberId?.value },
        establishment: { header: this.translateService.instant('excel.pointOfSale'), body: establishmentId?.value },
        externalCommercial: {
          header: this.translateService.instant('excel.comercialExt'),
          body: externalCommercialId?.value,
        },
        customerDetail: {
          header: this.translateService.instant('excel.detailLevel'),
          body: this.getCustomerDetailTranslation(this.searchCriteria.customerDetail),
        },
      };

      let coordY = secondRowYCoordinate;
      Object.keys(translationHeader).forEach((key) => {
        const { header, body } = translationHeader[key as keyof PdfSearchDTO];
        if (header && body) {
          doc.text(`${header} ${body}`, secondRowXCoordinate, coordY);
          coordY += 5;
        }
      });
    }

    autoTable(doc, {
      startY: startYTable,
      head: this.allData[0].header,
      headStyles: { fillColor: '#969696' },
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
      margin: { top: topMargin, left: 10, right: 10 },
      body: dataPdf.map(Object.values),
      styles: { fontSize: 8 },
      didDrawPage: (data) => {
        /*
          Check if the table has an already existing header so it won't be repeated:
            1. If there is no previous table, add the header.
            2. If the page is different from the first one and different from top margin, add the header.
            3. If the starting table Y coordinate is the top margin, add the header.
        */
        if (!lastAutoTable || (data.pageNumber > 1 && startYTable !== topMargin) || startYTable === topMargin) {
          //TITLE
          const headerXCoordinate: number = 80;
          const headerYCoordinate: number = 20;
          doc.setFontSize(24);
          doc.setTextColor('1E90FF');
          doc.text(this.translateService.instant('excel.dashboard.title'), headerXCoordinate, headerYCoordinate);

          //IMAGE
          const img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 10, 58, 20);

          //First row
          doc.setTextColor('#000000');
          doc.setFontSize(12);

          const firstRowYCoordinate: number = 35;
          const titleXCoordinate: number = 75;
          doc.text(this.translateService.instant('actionAgent.title'), titleXCoordinate, firstRowYCoordinate);
          doc.setFontSize(10);

          const thirdColumnXCoordinate: number = 150;
          doc.text(
            this.translateService.instant('excel.date') + moment().locale('es').format('DD-MM-YYYY HH:mm:ss'),
            thirdColumnXCoordinate,
            firstRowYCoordinate - 3
          );
          doc.text(
            this.translateService.instant('excel.pag') + doc.getNumberOfPages(),
            thirdColumnXCoordinate,
            firstRowYCoordinate + 3
          );
        }
        if (data.pageNumber === 1) doc.text(tableTitle, 10, startYTable - 2);
      },
    });

    return doc;
  }

  exportExcel(): void {
    this.searchCriteria = this.updateSearchCriteria(this.actionAgentForm);
    this.getPreparedData(false);
    this.excelService.exportExcelActionAgent(this.allData, this.searchCriteria);
  }

  private getCustomerDetailTranslation(customerDetail: string): string {
    let translatedText;
    switch (customerDetail) {
      case CustomerDetail.C:
        translatedText = this.translateService.instant('actionAgent.group');
        break;
      case CustomerDetail.P:
        translatedText = this.translateService.instant('actionAgent.dealer');
        break;
      default:
        translatedText = this.translateService.instant('actionAgent.pointOfSale');
        break;
    }
    return translatedText;
  }

  private getPreparedData(isPdf: boolean) {
    const filename =
      this.translateService.instant('actionAgent.pdf.title') + moment().locale(LANG_ES).format(DATE_FORMAT_DDMMYYYY);
    this.allData = [];
    this.allData = [
      ...this.tableStoreData.map((value, index) => {
        const columna = value.column.map((item) => {
          return [...item];
        });
        const cabecera = value.header.map((item) => {
          return [...item];
        });
        const nivelDeDetalle = this.response?.groups[index]?.group;
        let nivelDeDetalleTraducido = nivelDeDetalle;
        if (nivelDeDetalle.includes('Grupo')) {
          nivelDeDetalleTraducido = nivelDeDetalle.replace('Grupo', this.translateService.instant('actionAgent.group'));
        } else if (nivelDeDetalle.includes('Concesionario')) {
          nivelDeDetalleTraducido = nivelDeDetalle.replace(
            'Concesionario',
            this.translateService.instant('actionAgent.dealer')
          );
        } else if (nivelDeDetalle.includes('Punto de Venta')) {
          nivelDeDetalleTraducido = nivelDeDetalle.replace(
            'Punto de Venta',
            this.translateService.instant('actionAgent.pointOfSale')
          );
        }
        return {
          fileName: filename,
          column: [...columna],
          header: [...cabecera],
          title: nivelDeDetalleTraducido,
        };
      }),
    ];
    this.allData.forEach((item) => {
      item.column.forEach((eachColumn: any, index: number) => {
        if (index % 2 == 0) {
          eachColumn.unshift(this.translateService.instant(this.firstColumns[index / 2]));
        } else {
          eachColumn.unshift('');
        }
      });
      item.header.forEach((eachHeader: any) => {
        eachHeader.unshift(this.translateService.instant('excel.period'));
      });
    });

    this.allData.forEach((item) => {
      item.column = item.column.map((element: any) => {
        return element.map((elementOfArray: any) => {
          if (typeof elementOfArray === 'number') {
            return this.numberPipe.transform(elementOfArray);
          } else {
            return elementOfArray;
          }
        });
      });
    });
  }

  private buildRequest = () => {
    let request: any = {
      establishmentId: this.actionAgentForm.controls.pointOfSale?.value,
      externalCommercialUserId: this.actionAgentForm.controls.externalCommercial?.value,
      prescriberId: this.actionAgentForm.controls.nifcif?.value,
      chainId: this.actionAgentForm.controls.group?.value,
      officeId: this.actionAgentForm.controls.office?.value,
    };

    if (this.isBusinessDirector) {
      request = this.actionAgentForm.controls[FORM_PARAM_MACRO_CHAIN].value
        ? { ...request, chainId: this.actionAgentForm.controls[FORM_PARAM_MACRO_CHAIN].value, userType: CHAIN }
        : {
            ...request,
            officeId: this.actionAgentForm.controls[FORM_PARAM_MACRO_OFFICE].value,
            userType: OFFICE_DIRECTOR,
          };
    } else if (this.isMacroChainUser)
      request = { ...request, chainId: this.actionAgentForm.controls[FORM_PARAM_MACRO_CHAIN].value, userType: CHAIN };
    else if (this.isMacroOfficeUser)
      request = {
        ...request,
        officeId: this.actionAgentForm.controls[FORM_PARAM_MACRO_OFFICE].value,
        userType: OFFICE_DIRECTOR,
      };

    Object.keys(request).forEach((property) =>
      request[property] === null || request[property] === undefined ? (request[property] = '') : null
    );
    return request;
  };

  private getAllCombos = () => {
    Promise.all([
      this.getDivision(),
      this.getOffice(),
      this.getGroup(),
      this.getNifCif(),
      this.getPointOfSale(),
      this.getExternalCommercial(),
    ])
      .catch((error) => console.error(error))
      .finally(() => this.loaderGenericService.stopSpinner());
  };

  private getDivision() {
    this.combosStore.getDivisions().then(
      (data) => {
        this.divisionList = data;
        if (this.divisionList.length === 1) {
          this.actionAgentForm.controls.division.setValue(this.divisionList[0].id);
        }
      },
      (error) => console.error(error)
    );
  }

  private getOffice() {
    let filter = this.buildRequest();
    return this.combosStore.getOffices(filter).then(
      (data) => {
        if (data) {
          this.officeList = data.length === 0 ? [emptyCommonDataDTO] : [emptyCommonDataDTO, ...data];
          if (this.officeList.length === 2) {
            this.actionAgentForm.controls.office.setValue(this.officeList[1].id);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private getGroup() {
    let filter = this.buildRequest();
    return this.combosStore.getChains(filter).then(
      (data) => {
        if (data) {
          this.groupList = data.length === 0 ? [emptyCommonDataDTO] : [emptyCommonDataDTO, ...data];
          if (this.groupList.length === 2) {
            this.actionAgentForm.controls.group.setValue(this.groupList[1].id);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private getNifCif() {
    let filter: any = this.buildRequest();
    return this.combosStore.getPrescribers(filter).then(
      (data) => {
        if (data) {
          this.nifcifList = data.length === 0 ? [emptyCommonDataDTO] : [emptyCommonDataDTO, ...data];
          if (this.nifcifList.length === 2) {
            this.actionAgentForm.controls.nifcif.setValue(this.nifcifList[1].id);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private getPointOfSale() {
    let filter: any = this.buildRequest();
    return this.combosStore.getEstablishments(filter).then(
      (data) => {
        if (data) {
          this.pointOfSaleList = data.length === 0 ? [emptyCommonDataDTO] : [emptyCommonDataDTO, ...data];
          if (this.pointOfSaleList.length === 2) {
            this.actionAgentForm.controls.pointOfSale.setValue(this.pointOfSaleList[1].id);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private getExternalCommercial() {
    let filter: any = this.buildRequest();
    return this.controlPanelStore.getListExternalCommercialByFilter(filter).then(
      (data) => {
        if (data) {
          this.externalCommercialList = data.length === 0 ? [emptyCommonDataDTO] : [emptyCommonDataDTO, ...data];
          if (this.externalCommercialList.length === 2) {
            this.actionAgentForm.controls.externalCommercial.setValue(this.externalCommercialList[1].id);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private getMacroChains = async () => {
    this.resetForms();
    this.actionAgentForm.controls[FORM_PARAM_MACRO_CHAIN].reset();
    this.macroChainList = [];
    const promise = this.isBusinessDirector
      ? this.combosStore.getChainsByDivision(this.userInfo.division?.toString())
      : this.combosStore.getChainsFromMacroUser(this.userInfo.userId);
    promise
      .then(
        (data) => {
          if (data) {
            this.macroChainList =
              this.isBusinessDirector && data.length > 0 ? [{ id: undefined, value: '' }, ...data] : [...data];
            if (this.macroChainList.length === 1)
              this.actionAgentForm.controls[FORM_PARAM_MACRO_CHAIN].setValue(this.macroChainList[0]?.id);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => {
        if (error) {
          console.error(error);
        }
      });
  };

  private getMacroOffices = async () => {
    this.resetForms();
    this.actionAgentForm.controls[FORM_PARAM_MACRO_OFFICE].reset();
    this.macroOfficeList = [];
    const promise = this.isBusinessDirector
      ? this.combosStore.getOfficesByDivision(this.userInfo.division?.toString())
      : this.combosStore.getOfficesFromMacroUser(this.userInfo.userId);
    promise
      .then(
        (data) => {
          if (data) {
            this.macroOfficeList =
              this.isBusinessDirector && data.length > 0 ? [{ id: undefined, value: '' }, ...data] : [...data];
            if (this.macroOfficeList.length === 1)
              this.actionAgentForm.controls[FORM_PARAM_MACRO_OFFICE].setValue(this.macroOfficeList[0]?.id);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => {
        if (error) {
          console.error(error);
        }
      });
  };

  private clearOffice(): void {
    this.officeList = [];
    this.actionAgentForm.controls.office.reset();
  }

  private clearGroup(): void {
    this.groupList = [];
    this.actionAgentForm.controls.group.reset();
  }

  private clearNifcif(): void {
    this.nifcifList = [];
    this.actionAgentForm.controls.nifcif.reset();
  }

  private clearPointOfSale(): void {
    this.pointOfSaleList = [];
    this.actionAgentForm.controls.pointOfSale.reset();
  }

  private clearExternalCommercial(): void {
    this.externalCommercialList = [];
    this.actionAgentForm.controls.externalCommercial.reset();
  }

  private clearAll(): void {
    this.clearOffice();
    this.clearGroup();
    this.clearNifcif();
    this.clearPointOfSale();
    this.clearExternalCommercial();
  }

  private addGroupData(
    headers: string[][],
    rows: number[][],
    groupData: ActionAgentTotal,
    yearIndex: number,
    rowOffset: number
  ) {
    headers[yearIndex].push(groupData.year);
    this.pushData(rows, rowOffset, groupData);
  }

  private addMonthlyData(
    headers: string[][],
    rows: number[][],
    monthlyData: ActionAgentMonth[],
    yearIndex: number,
    rowOffset: number
  ) {
    monthlyData.forEach((monthlyReport) => {
      headers[yearIndex].push(monthlyReport.monthYear);
      this.pushData(rows, rowOffset, monthlyReport);
    });
  }

  private pushData(rows: number[][], rowOffset: number, data: ActionAgentTotal | ActionAgentMonth) {
    rows[rowOffset].push(data.investment);
    rows[rowOffset + 2].push(data.numOps);
    rows[rowOffset + 4].push(data.flexic);
    rows[rowOffset + 6].push(data.ppt);
    rows[rowOffset + 8].push(data.ppb);
    rows[rowOffset + 10].push(data.ppp);
    rows[rowOffset + 12].push(data.tir);
    rows[rowOffset + 14].push(data.totalCommision);
    rows[rowOffset + 16].push(data.safeCommision);
    rows[rowOffset + 18].push(data.feeCommision);
    rows[rowOffset + 20].push(data.usoInfo);
  }

  private formatDataSource(group: ActionAgentReportDTO): TableData {
    const headers: string[][] = [[], []];
    const rows: number[][] = Array.from({ length: MAX_NUM_ROWS }, () => [] as Array<number>);

    this.addGroupData(headers, rows, group.totalCurrent, CURRENT_YEAR, MAX_NUM_ROWS - 22);
    this.addMonthlyData(headers, rows, group.current, CURRENT_YEAR, MAX_NUM_ROWS - 22);

    this.addGroupData(headers, rows, group.totalPrevious, LAST_YEAR, MAX_NUM_ROWS - 21);
    this.addMonthlyData(headers, rows, group.previous, LAST_YEAR, MAX_NUM_ROWS - 21);

    return { title: group?.group, column: rows, header: headers };
  }

  hasNoOperation() {
    if (this.table && this.isCalledOnce) {
      return this.table.length === 0;
    }
  }

  isNifCifGenericSelect() {
    return this.nifcifList?.length < MAX_SELECTOR_DATA;
  }

  isPointOfSaleGenericSelect() {
    return this.pointOfSaleList?.length < MAX_SELECTOR_DATA;
  }

  onPageEvent(event: PageEvent) {
    const { pageIndex } = event;
    // Since the minimum value of page index is 1 and MatPaginator starts from 0, it needs to be incremented by 1.
    this._currentPageIndex = pageIndex + 1;

    if (this._currentPageIndex <= this._maxPageIndex) {
      //If current page index is less than the maximum reached, load the previous response
      this.getPreviousResponsePagination(this._currentPageIndex);
    } else {
      // Store the value from page index so it will not be repeated in the same calls since response will be store when using pagination.
      this._maxPageIndex = this._currentPageIndex;
      this.consult(true);
    }
  }

  private resetForms = (filter?: string[]) => {
    if (this.actionAgentForm) {
      Object.keys(this.actionAgentForm.controls).forEach((key) => {
        if (filter) {
          if (!filter.includes(key)) {
            this.actionAgentForm.controls[key]?.reset();
          }
        } else this.actionAgentForm.controls[key]?.reset();
      });
    }
  };

  private initValueChangesMacroUser = (property: string) => {
    this.actionAgentForm.controls[property].valueChanges.subscribe((value) => {
      if (value) {
        this.resetForm();
        this.resetAllComboLists();
        this.getAllCombos();
        if (this.isBusinessDirector)
          this.disableFormControl(
            property === FORM_PARAM_MACRO_CHAIN ? FORM_PARAM_MACRO_OFFICE : FORM_PARAM_MACRO_CHAIN
          );
        this.enableFormControls();
      } else {
        this.resetAllComboLists();
        if (this.isBusinessDirector)
          this.enableFormControl(
            property === FORM_PARAM_MACRO_CHAIN ? FORM_PARAM_MACRO_OFFICE : FORM_PARAM_MACRO_CHAIN
          );
        this.disableFormControls();
      }
    });
  };

  private resetForm = () => {
    Object.keys(this.actionAgentForm.controls).forEach((property) => {
      if (
        property !== FORM_PARAM_MACRO_CHAIN &&
        property !== FORM_PARAM_MACRO_OFFICE &&
        this.actionAgentForm.controls[property].value
      ) {
        this.actionAgentForm.controls[property].reset();
      }
    });
  };

  private resetAllComboLists = () => {
    this.divisionList = [];
    this.officeList = [];
    this.groupList = [];
    this.nifcifList = [];
    this.pointOfSaleList = [];
    this.externalCommercialList = [];
  };

  private enableFormControl = (param: string) => {
    if (param) {
      const form = this.actionAgentForm.controls;
      if (form[param]?.disabled) form[param].enable();
    }
  };

  private enableFormControls = () => {
    if (this.actionAgentForm) {
      Object.keys(this.actionAgentForm.controls).forEach((key) => {
        if (
          key !== FORM_PARAM_MACRO_CHAIN &&
          key !== FORM_PARAM_MACRO_OFFICE &&
          this.actionAgentForm.controls[key].disabled
        ) {
          this.actionAgentForm.controls[key].enable();
        }
      });
    }
  };

  private disableFormControl = (param: string) => {
    if (param) {
      const form = this.actionAgentForm.controls;
      if (form[param]?.enabled) {
        if (form[param].value) form[param].reset();
        form[param].disable();
      }
    }
  };

  private disableFormControls = () => {
    if (this.actionAgentForm) {
      Object.keys(this.actionAgentForm.controls).forEach((key) => {
        if (key !== FORM_PARAM_MACRO_CHAIN && key !== FORM_PARAM_MACRO_OFFICE) {
          this.actionAgentForm.controls[key].reset();
          if (this.actionAgentForm.controls[key].enabled) {
            this.actionAgentForm.controls[key].disable();
          }
        }
      });
    }
  };

  private initMacroUsers = (formProperties: string[]) => {
    this.disableFormControls();
    formProperties.forEach((property) => {
      this.actionAgentForm.addControl(property, new FormControl('', Validators.required));
      this.initValueChangesMacroUser(property);
      if (property === FORM_PARAM_MACRO_CHAIN) this.getMacroChains();
      else if (property === FORM_PARAM_MACRO_OFFICE) this.getMacroOffices();
    });
  };

  private findComboDataByValue(value: string, list: CommonDataDTO[], defaultValue: CommonDataDTO): CommonDataDTO {
    if (value === null || value.length <= 0) return defaultValue;

    const result = list.filter((item) => item.id === value);
    return result.length > 0 ? result[0] : defaultValue;
  }

  private clearConsultValues() {
    this.response = null;
    this.table = [];
    this.tableStoreData = [];
    this.itemsLength = 0;
    this._maxPageIndex = 1;
    this._currentPageIndex = 1;
    this.searchCriteria = this.updateSearchCriteria(this.actionAgentForm);
  }

  private async getPreviousResponsePagination(pageIndex: number): Promise<void> {
    const firstIndexItem: number = MIN_PAGE_SIZE * (pageIndex - 1);
    const lastIndexItem: number = firstIndexItem + MIN_PAGE_SIZE;
    if (this.tableStoreData) this.table = [...this.tableStoreData.slice(firstIndexItem, lastIndexItem)];
    else console.error('No previous stored data found');
  }
}
